import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { api } from "@/lib/auth";
import { eur } from "@/lib/constants";

// Slugs disposant d'une nouvelle interface dédiée (configurateur ou parcours),
// remplaçant l'ancien formulaire /commander/:slug.
const CTA_OVERRIDES = {
  "planche-pdf-prete": { to: "/configurer/planche", label: "Configurer ma planche" },
  "adapter-format":    { to: "/configurer/format",  label: "Adapter mon fichier" },
  "fichier-conforme":  { to: "/configurer/conforme", label: "Rendre mon fichier conforme" },
  "controle-prepresse": { to: "/configurer/prepresse", label: "Configurer mon contrôle prépresse" },
  "premiere-de-couverture": { to: "/configurer/premiere-de-couverture", label: "Commander ma première de couverture" },
  "couverture-complete": { to: "/configurer/couverture-complete", label: "Commander ma couverture complète" },
  "adapter-couverture": { to: "/configurer/adapter-couverture", label: "Adapter ma couverture existante" },
  "creation-support":  { to: "/creer-mon-support",   label: "Créer mon support" },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setErr("Prestation introuvable"));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto p-16 text-center">{err}</div>;
  if (!svc) return <div className="max-w-3xl mx-auto p-16 text-center font-mono text-sm">Chargement…</div>;

  const cta = CTA_OVERRIDES[svc.slug] || { to: `/commander/${svc.slug}`, label: "Commander cette prestation" };

  return (
    <div data-testid={`page-service-${svc.slug}`}>
      <section className="cs-cream border-b border-black/10 py-16 lg:py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="cs-eyebrow text-neutral-500">Prestation · {svc.category}</div>
            <h1 className="cs-h1 mt-4 text-4xl sm:text-5xl lg:text-6xl">{svc.name}</h1>
            <p className="mt-6 font-serif text-xl lg:text-2xl max-w-2xl leading-snug">{svc.short_description}</p>
          </div>
          <aside className="lg:col-span-4">
            <div className="cs-card p-8 sticky top-24">
              <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">À partir de</div>
              <div className="font-serif text-5xl mt-2">{eur(svc.starting_price)}</div>
              {svc.price_note && <p className="mt-3 text-[13px] text-neutral-600">{svc.price_note}</p>}
              {svc.tiers?.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {svc.tiers.map((t, i) => (
                    <li key={i} className={`flex justify-between text-sm border-b border-black/10 pb-2 ${t.highlight ? "text-black font-medium" : "text-neutral-600"}`}>
                      <span className="flex items-center gap-2">
                        {t.highlight && <span className="inline-block h-1.5 w-1.5 bg-[#0E7C90]" />}
                        {t.label}
                      </span>
                      <span className="font-mono">{t.note || `${t.price} €`}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="cs-rule my-6" />
              <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">Délai</div>
              <div className="mt-1 text-sm">{svc.delay}</div>
              {svc.revisions && (
                <>
                  <div className="mt-4 text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">Corrections</div>
                  <div className="mt-1 text-sm">{svc.revisions}</div>
                </>
              )}
              <div className="mt-6 flex flex-col gap-3">
                <Link to={cta.to} data-testid="cta-order" className="cs-btn-primary justify-center">
                  {cta.label} <ArrowUpRight size={18} />
                </Link>
                <Link to="/demande-devis" data-testid="cta-quote" className="cs-btn-ghost !text-black !border-black justify-center">
                  J'ai une question
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="cs-cream py-16 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12">
          {svc.problem && (
            <div>
              <div className="cs-eyebrow text-neutral-500">Le problème</div>
              <p className="mt-4 font-serif text-2xl leading-snug">{svc.problem}</p>
            </div>
          )}
          {svc.result && (
            <div>
              <div className="cs-eyebrow text-neutral-500">Le résultat</div>
              <p className="mt-4 font-serif text-2xl leading-snug">{svc.result}</p>
            </div>
          )}
        </div>
      </section>

      <section className="cs-dark cs-grain py-20 border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="cs-eyebrow text-neutral-400">Ce qui est inclus</div>
            <ul className="mt-6 space-y-3">
              {svc.included.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px]">
                  <Check size={18} className="mt-1 text-[#0E7C90]" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="cs-eyebrow text-neutral-400">Ce que vous fournissez</div>
            <ul className="mt-6 space-y-3">
              {svc.client_provides.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px]">
                  <span className="mt-2 h-1.5 w-1.5 bg-[#0E7C90] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            {svc.options?.length > 0 && (
              <>
                <div className="cs-eyebrow text-neutral-400 mt-10">Options</div>
                <ul className="mt-6 space-y-2">
                  {svc.options.map((o, i) => (
                    <li key={i} className="flex justify-between border-b border-white/10 py-3 text-sm">
                      <span>{o.label}</span>
                      <span className="font-mono">+ {eur(o.price)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="cs-cream py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
          <h3 className="cs-h2 text-4xl lg:text-5xl">Prêt à démarrer&nbsp;?</h3>
          <p className="mt-4 text-neutral-700 max-w-xl mx-auto">
            Une prestation au tarif fixe couvre le périmètre annoncé. Si votre fichier nécessite davantage de travail,
            Charbonier Studio vous proposera une estimation complémentaire avant toute intervention supplémentaire.
          </p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to={cta.to} className="cs-btn-primary">{cta.label} <ArrowUpRight size={18} /></Link>
            <Link to="/demande-devis" className="cs-btn-ghost !text-black !border-black">Demander un devis personnalisé</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
